import React, { useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useMappedState } from "redux-react-hook";
import { Link } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import {
  Button,
  Grid,
  GridItem,
  PageSection,
  PageSectionVariants,
  Title,
  TextVariants,
  TextContent,
  Text,
  SelectOption,
  Level,
  LevelItem,
} from "@patternfly/react-core";
import { Spinner } from "@patternfly/react-core";
import MultiTypeaheadSelectInput from "../components/general/MultipleSelect";
import ProductTab from "../components/product/ProductTab";
import ProductTable from "../components/product/ProductTable";
import ProductInfo from "../components/product/ProductInfo";
import {
  REQUESTED_PRODUCTS,
  REQUESTED_RESET_PRODUCT_STATUS,
} from "../redux/actions/productAction";
import { REQUESTED_RESET_VERSION_STATUS } from "../redux/actions/versionAction";
import { REQUESTED_PHASES } from "../redux/actions/phaseAction";
import { isAdmin, isProductManager } from "../utils/userUtil";

export default () => {
  const RHEL = "Red Hat Enterprise Linux";

  const mapState = useCallback(
    (state) => ({
      productsMap: state.productState.productsMap,
      isFetchingProducts: state.productState.isFetchingProducts,
      isUpdatedProduct: state.productState.isUpdatedProduct,
      isUpdatedProductVersions: state.productState.isUpdatedProductVersions,
      isDeletingProduct: state.productState.isDeletingProduct,
      isDeletedProduct: state.productState.isDeletedProduct,
      phaseList: state.phaseState.phaseList,
      currentUser: state.userState.currentUser,
      isUpdatedVersion: state.versionState.isUpdatedVersion,
    }),
    []
  );

  const {
    productsMap,
    isFetchingProducts,
    isUpdatedProduct,
    isUpdatedProductVersions,
    isDeletedProduct,
    phaseList,
    currentUser,
    isUpdatedVersion,
  } = useMappedState(mapState);
  const dispatch = useDispatch();

  // load selectedProducts from cookies
  const [cookies, setCookie] = useCookies(["selectedProducts"]);
  const [selectedProducts, setSelectedProducts] = useState(
    cookies.selectedProducts && cookies.selectedProducts.length > 0
      ? cookies.selectedProducts
      : null
  );

  const [productSelectOptions, setProductSelectOptions] = useState([]);

  useEffect(() => {
    if (!productsMap) {
      dispatch({
        type: REQUESTED_PRODUCTS,
        payload: {},
      });
    }
  }, [dispatch, productsMap]);

  useEffect(() => {
    if (
      isUpdatedProduct ||
      isUpdatedProductVersions ||
      isDeletedProduct ||
      isUpdatedVersion
    ) {
      dispatch({
        type: REQUESTED_PRODUCTS,
        payload: {},
      });
    }

    return function cleanup() {
      dispatch({ type: REQUESTED_RESET_PRODUCT_STATUS });
      dispatch({ type: REQUESTED_RESET_VERSION_STATUS });
    };
  }, [
    dispatch,
    isUpdatedProduct,
    isUpdatedProductVersions,
    isDeletedProduct,
    isUpdatedVersion,
  ]);

  useEffect(() => {
    if (phaseList.length < 1) {
      dispatch({
        type: REQUESTED_PHASES,
        payload: {},
      });
    }
  }, [dispatch, phaseList]);

  // if selectedProducts is not set in cookie
  // default set to the first product is products array
  // otherwise remove product not exists in products array
  useEffect(() => {
    let productOptions = [];
    let newSelectedProducts = selectedProducts ? [...selectedProducts] : [];

    if (!isEmpty(productsMap) && !isFetchingProducts) {
      productOptions = Object.keys(productsMap).map((key) => ({
        id: productsMap[key].id,
        value: key,
        disabled: false,
      }));

      if (!selectedProducts || selectedProducts.length === 0) {
        newSelectedProducts = productsMap[RHEL]
          ? [productsMap[RHEL].name]
          : [Object.keys(productsMap)[0].name];
      } else {
        newSelectedProducts = selectedProducts.filter(
          (product) => !!productsMap[product]
        );
      }
      setSelectedProducts(newSelectedProducts);
      setCookie("selectedProducts", newSelectedProducts,{ path: '/' });
    }

    setProductSelectOptions(
      productOptions.map((option, index) => (
        <SelectOption
          isDisabled={option.disabled}
          key={`product-${index}`}
          value={option.value}
        />
      ))
    );
  }, [productsMap, isFetchingProducts]);

  const renderProductTable = useCallback(
    (product) => {
      if (product && productsMap && productsMap[product]) {
        return (
          <GridItem key={`product-${product}`} span={12}>
            <ProductInfo
              key={`product-${product}-info`}
              product={productsMap[product]}
              productId={productsMap[product].id}
            />
            <ProductTable
              key={`product-${product}-table`}
              productId={productsMap[product].id}
            />
          </GridItem>
        );
      }
    },
    [productsMap]
  );

  const renderProductTables = useCallback(() => {
    if (selectedProducts && selectedProducts.length > 0) {
      return selectedProducts.map((product) => renderProductTable(product));
    }
  }, [selectedProducts, renderProductTable]);

  const onSelectProduct = useCallback((selectedProducts) => {
    setSelectedProducts(selectedProducts);
    setCookie("selectedProducts", selectedProducts);
  }, []);

  const renderCreateButton = useCallback(() => {
    if (isAdmin(currentUser.roles) || isProductManager(currentUser.roles)) {
      return (
        <LevelItem>
          <Link to="/product/create">
            <Button variant="primary" className="button--MarginLeft">
              Create Product
            </Button>
          </Link>
        </LevelItem>
      );
    }
  }, [currentUser]);

  const renderSelectProduct = useCallback(() => {
    if (selectedProducts && !isEmpty(productSelectOptions)) {
      return (
        <MultiTypeaheadSelectInput
          titleId="product-multi-select"
          placeholder="Select product"
          options={productSelectOptions}
          selected={selectedProducts}
          onSelect={(selected) => onSelectProduct(selected)}
        />
      );
    }
  }, [selectedProducts, onSelectProduct, productSelectOptions]);

  const renderHeader = useCallback(() => {
    if (isFetchingProducts) {
      return <Spinner size="xl" />;
    } else {
      return (
        <>
          <ProductTab />
          <Title
            headingLevel="h1"
            size="4xl"
            className="pf-c-title--PaddingTop"
          >
            Product Life Cycle
          </Title>
          <TextContent>
            <Text component={TextVariants.h2}>Choose products here:</Text>
          </TextContent>
          <Level hasGutter>
            <LevelItem>{renderSelectProduct()}</LevelItem>
            {renderCreateButton()}
          </Level>
        </>
      );
    }
  }, [
    productsMap,
    isFetchingProducts,
    renderSelectProduct,
    renderCreateButton,
  ]);

  const renderProductBody = useCallback(() => {
    // render product table when productsMap is fetched
    if (
      // !productsMap ||
      isFetchingProducts || 
      // !selectedProducts ||
      phaseList.length < 1
    ) {
      return <Spinner size="xl" />;
    } else {
      return renderProductTables();
    }
  }, [
    // productsMap,
    isFetchingProducts,
    // selectedProducts,
    phaseList,
    renderProductTables,
  ]);

  useEffect(() => {
    renderProductTables();
  }, [
    selectedProducts,
    isUpdatedProduct,
    isUpdatedProductVersions,
    renderProductTables,
  ]);

  const renderBody = useCallback(() => {
    return (
      <>
        <PageSection variant={PageSectionVariants.light}>
          {renderHeader()}
        </PageSection>
        <PageSection>
          <Grid hasGutter key={`product-grid`}>
            {renderProductBody()}
          </Grid>
        </PageSection>
      </>
    );
  }, [renderHeader, renderProductBody]);

  useEffect(() => {
    if (productsMap) {
      renderBody();
    }
  }, [productsMap, renderBody]);

  return renderBody();
};
